import StatusTag from '@/components/_common/StatusTag';
import { InvoiceSpedStatusRegisteredConstants } from '@/core/domain/invoice/invoices/invoices.enums';
import { usePurchaseSpedList } from '@/core/domain/purchase/purchase-sped/purchase-sped.hooks';
import {
  useSpedDocumentManageImports,
  useSpedDocumentPurchases,
} from '@/core/domain/sped/sped-documents/sped-documents.hooks';
import { SpedDocumentStatus } from '@/core/domain/sped/sped-documents/sped-documents.types';
import { useDisclosure } from '@/hooks';
import { useSpedDocumentContext } from '@/pages/FiscalPages/SpedPage/SpedDocumentsPage/SpedDocumentDetailPage';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Alert, Button, Modal, Table, Typography } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

export default function SpedDocumentDetailImportPurchase() {
  const spedDocument = useSpedDocumentContext();

  const hasImportPermission =
    hasPermissions([Authorities.SPED_DOCUMENT_IMPORT_PURCHASE]) &&
    !!spedDocument?.status &&
    [
      SpedDocumentStatus.AWAITING_SELECTION,
      SpedDocumentStatus.REOPENED,
      SpedDocumentStatus.REJECTED,
    ].includes(spedDocument?.status);

  const periodLabel = moment([
    Number(spedDocument?.assessmentYear),
    Number(spedDocument?.assessmentMonth) - 1,
    1,
  ]).format('MMMM/YYYY');

  const [opened, action] = useDisclosure();

  const history = useHistory();

  const [idsSelected, setIdsSelected] = useState<number[]>([]);

  const spedDocumentManageImports = useSpedDocumentManageImports();

  const { data: purchases, isLoading } = usePurchaseSpedList(
    {
      companyId: spedDocument?.companyId as number,
      month: spedDocument?.assessmentMonth as string,
      year: spedDocument?.assessmentYear as string,
    },
    opened,
  );

  const { data: purchasesImported } = useSpedDocumentPurchases({
    spedDocumentId: spedDocument?.id as number,
  });

  const hasPurcheseNotEligible = purchases?.some(
    (purchase) => !purchase.isEligible,
  );

  useEffect(() => {
    if (purchases?.length) {
      setIdsSelected(
        purchases
          .filter((purchase) => purchase.isEligible)
          .filter(
            (purchase) =>
              !purchasesImported?.some(
                (imported) => imported.id === purchase.id,
              ),
          )
          .map((purchase) => purchase.id),
      );
    }
  }, [purchases, purchasesImported]);

  const handleClose = () => {
    setIdsSelected([]);
    action.close();
  };

  const handleSubmit = async () => {
    if (!spedDocument) return;

    await spedDocumentManageImports.mutateAsync({
      importType: 'PURCHASE',
      spedDocumentId: spedDocument?.id,
      items: idsSelected.map((id) => ({
        entityId: id,
        entityEnum: 'PURCHASE',
      })),
    });

    handleClose();
    history.push('?tab=purchases');
  };

  return (
    <>
      <Button
        onClick={action.open}
        block
        type="primary"
        disabled={!hasImportPermission}
      >
        Importar compras
      </Button>

      <Modal
        width={900}
        open={opened}
        onCancel={handleClose}
        title="Importação de notas de compra"
        okText="Importar"
        okButtonProps={{ disabled: idsSelected.length < 1 }}
        onOk={handleSubmit}
        confirmLoading={spedDocumentManageImports.isLoading}
      >
        {hasPurcheseNotEligible && (
          <Alert
            style={{ marginBottom: 24 }}
            type="warning"
            description="Compras relacionado ao periodo de apuração do SPED sem vinculo com o XML da nota fiscal não poderão ser importadas."
          />
        )}

        <Typography.Title level={4}>
          Compras emitidas do período ({periodLabel})
        </Typography.Title>

        <Table
          loading={isLoading}
          dataSource={purchases}
          pagination={false}
          rowKey={(item) => item.id}
          scroll={{ x: true }}
          rowSelection={{
            selectedRowKeys: idsSelected,
            onChange: (keys) => setIdsSelected(keys as number[]),
            getCheckboxProps: (item) => ({
              disabled:
                !item.isEligible ||
                spedDocumentManageImports.isLoading ||
                !!purchasesImported?.some(
                  (imported) => imported.id === item.id,
                ),
            }),
          }}
          columns={[
            { title: 'Código', dataIndex: 'code', width: 200 },
            {
              title: 'Fornecedor',
              dataIndex: 'supplierName',
              width: 300,
              ellipsis: true,
            },
            {
              title: 'XML vinculado',
              width: 200,
              render: (_, item) => (
                <StatusTag
                  color={item.isEligible ? 'green' : 'default'}
                  label={item.isEligible ? 'Com XML' : 'Sem XML'}
                />
              ),
            },
            {
              title: 'Status',
              render: (_, item) => {
                const info =
                  InvoiceSpedStatusRegisteredConstants[
                    item.statusRegisteredInPlugStorage
                  ];

                return <StatusTag color={info.color} label={info.label} />;
              },
            },
          ]}
        />
      </Modal>
    </>
  );
}
